import { Fragment, useEffect, useState, type ReactNode } from "react";
import {
  ArrowRight, Bell, CalendarClock, Check, CircleHelp, ExternalLink, FileText, Hourglass, ListChecks,
  MessageCircleReply, MonitorPlay, PenLine, Play, Radio, Scissors, Sparkles, TriangleAlert, Users,
} from "lucide-react";
import {
  AddToCalendar, Avatar, Btn, Card, Chip, CopyCode, CountdownBoxes, Empty, Hand, JoinButton, List, P, PLink,
  PortalShell, ProgressBar, ProgressRing, Row, Section, Segmented, SessionChip, Sheet, Thumb,
  can, atLeast, continueWatching, currentProject, fmtDay, getProject, getSession, fmtDur, fmtLeft, fmtTime, heroSession, lockLine,
  makesAt, newSince, nextClubSession, notesAt, pastProjects, pinnedNote, projectOf, projectRuntime, qaFor, relDay,
  sessionEnd, sessionState, sessionsFor, timeLabel, upcomingSessions, useChecklist, useCountdown, useNow, usePortal, who, ago,
  ME, PLANS, Polaroid, type Item, type Note, type Project, type Session,
} from "./Portal";
import "./Home.css";

/* P1 · Club Home (research §5 "Home content priority").

   Phone, top to bottom: pinned note / LIVE strip (shell) → Next-up hero (state
   machine: Upcoming → Class tonight → Live now → Processing → Replay ready) →
   this month's project → continue watching → From Chelsea → new since your last
   visit → past projects shelf.
   Desktop: the hero and my supply checklist move to the sticky right rail; the
   left column leads with the project card.

   Roles: Members get everything; Thread Friends / free accounts see the schedule
   and project with tier-named locks (and the free YouTube lives instead of
   Continue watching); visitors get a compact welcome + plans; Chelsea gets a
   Studio "Today" variant. All local state; nothing is sent anywhere. */

/* ─────────────── helpers ─────────────── */

/** "Tonight · 7 PM MT · 9 PM ET" / "Tuesday, Oct 13 · 7 PM MT · 9 PM ET" for the viewer. */
function useWhen() {
  const { tz, viewer: v } = usePortal();
  const yours = v ? v.tzAbbr : "your time";
  return (s: Session, now: Date, short?: boolean) => {
    const rel = relDay(s.start, now, tz);
    const day = /^(Tonight|Today|Tomorrow)$/.test(rel) ? rel : short ? fmtDay(s.start, tz) : `${fmtDay(s.start, tz, { weekday: "long" })}, ${fmtDay(s.start, tz, { month: "short", day: "numeric" })}`;
    return `${day} · ${timeLabel(s.start, tz, yours)}`;
  };
}
/** Keeps each " · " segment of a time line on one line, so "9 PM ET" never splits. */
const nw = (text: string) => text.split(" · ").map((t, i) => <Fragment key={i}>{i > 0 && " · "}<span className="hm-nw">{t}</span></Fragment>);
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;
const shortMonth = (p: Project) => p.monthLabel.replace(/^(\w{3})\w*/, "$1");
const hourIn = (d: Date, tz: string) => Number(new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", hourCycle: "h23" }).format(d));
const overnight = (d: Date, tz: string) => { const h = hourIn(d, tz); return h < 7 || h >= 23; };
const greeting = (now: Date, tz: string) => {
  const h = hourIn(now, tz);
  return h < 5 ? "Good evening" : h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

/* ─────────────── Next-up hero ─────────────── */

function NextUp({ onChecklist }: { onChecklist: () => void }) {
  const { role, tz, viewer: v } = usePortal();
  const yours = v ? v.tzAbbr : "your time";
  const now = useNow(15000);
  const when = useWhen();
  const h = heroSession(now);
  if (!h) return <Empty icon={<CalendarClock size={22} />} title="No classes scheduled" body="The next project's dates go up at the start of the month." />;
  const { session: s, state } = h;
  const proj = projectOf(s);
  const member = can(role, "live");
  const after = state === "processing" || state === "replay" ? nextClubSession(now) : undefined;
  const next = after && after.id !== s.id ? after : undefined;
  const hand = { upcoming: "quilt your panels this weekend!", tonight: "see you at 7!", live: "come sew with us!", processing: "thank you for coming!", replay: "no rush, it's saved" }[state];

  return <Card tone={state === "live" ? "ink" : "teal"} pad="lg" tape="yellow" tapeSide="left" className={`hm-hero is-${state}`} as="section">
    <div className="hm-hero-top">
      <div className="hm-hero-head">
        <div className="hm-hero-tags"><SessionChip state={state} />{proj && <span className="pt-kicker">{proj.monthLabel.split(" ")[0]} project</span>}</div>
        <h2 className="hm-hero-title">{proj?.title ?? s.title}<span><span className="hm-sep"> · </span>{s.label}</span></h2>
        {(state === "upcoming" || state === "tonight") && <p className="hm-hero-when"><CalendarClock size={16} aria-hidden="true" /><span>{nw(when(s, now, true))}</span></p>}
      </div>
      {proj && <span className="hm-hero-pic" aria-hidden="true"><img src={proj.hero} alt="" /></span>}
    </div>
    <Hand className="hm-hero-hand">{hand}</Hand>

    {(state === "upcoming" || state === "tonight") && <>
      <CountdownBoxes to={s.start} tone="onDark" />
      {member ? <MemberPrep s={s} /> : <HeroLock what="live" />}
    </>}

    {state === "live" && <LiveBlock s={s} member={member} onChecklist={onChecklist} />}

    {state === "processing" && <ProcessingBlock s={s} member={member} />}

    {state === "replay" && s.replay && <ReplayBlock s={s} member={member} />}

    {next && <div className="hm-hero-next">
      <div><span className="hm-hero-next-k">Next</span><span className="hm-hero-next-t">{next.label} · {relDay(next.start, now, tz)}</span><span className="hm-hero-next-m">{nw(timeLabel(next.start, tz, yours))}</span></div>
      {member && <AddToCalendar session={next} size="sm" variant="secondary" onDark label="Add" />}
    </div>}

    {!member && <FreeLiveLine />}
  </Card>;
}

function MemberPrep({ s }: { s: Session }) {
  return <>
    <div className="hm-hero-actions">
      <Btn variant="pop" to={P.session(s.id)} iconEnd={<ArrowRight size={17} aria-hidden="true" />}>Class page</Btn>
      <AddToCalendar session={s} variant="secondary" onDark />
    </div>
  </>;
}

/** Tier-named lock for the dark hero card. */
function HeroLock({ what, length, preview, sessionId }: { what: "live" | "replay"; length?: number; preview?: boolean; sessionId?: string }) {
  const { role } = usePortal();
  return <div className="hm-hero-lock">
    <Chip tone="lock" icon={<LockGlyph />}>{lockLine("member", length, preview)}</Chip>
    <p>{what === "live"
      ? "Live classes are for Members: sew along and ask Chelsea questions as you go."
      : "Every replay comes with chapters, fix notes and the live chat."}
      {role === "friend" && " You keep everything you have now."}</p>
    <div className="hm-hero-actions">
      {role === "visitor" ? <JoinButton need="member" variant="secondary" onDark short /> : <JoinButton need="member" variant="pop" />}
      {preview && sessionId && <Btn variant="secondary" onDark to={P.session(sessionId)} icon={<Play size={16} aria-hidden="true" />}>Watch 30s preview</Btn>}
    </div>
  </div>;
}
const LockGlyph = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>;

function LiveBlock({ s, member, onChecklist }: { s: Session; member: boolean; onChecklist: () => void }) {
  const now = useNow(30000);
  const mins = Math.max(1, Math.round((now.getTime() - s.start.getTime()) / 6e4));
  const left = Math.max(0, Math.round((sessionEnd(s).getTime() - now.getTime()) / 6e4));
  return <>
    <p className="hm-hero-live"><span className="hm-pulse" aria-hidden="true" /><span>{nw(`Started ${mins}m ago · 14 sewing along · about ${fmtDur(left * 60)} to go`)}</span></p>
    {member ? <div className="hm-hero-actions">
      <Btn variant="live" size="lg" to={P.session(s.id)} icon={<Radio size={19} aria-hidden="true" />} className="hm-join">Join live class</Btn>
      <Btn variant="secondary" onDark icon={<ListChecks size={17} aria-hidden="true" />} onClick={onChecklist}>What you need</Btn>
    </div> : <HeroLock what="live" />}
  </>;
}

function ProcessingBlock({ s, member }: { s: Session; member: boolean }) {
  const { tz, viewer: v } = usePortal();
  const now = useNow(30000);
  const [notify, setNotify] = useState(true);
  const abbr = v ? ` ${v.tzAbbr}` : " your time";
  const end = sessionEnd(s);
  const eta = new Date(end.getTime() + 3 * 36e5);
  return <>
    <p className="hm-hero-body">Class wrapped {ago(end, now)}. The replay, chapters and chat replay usually take about 3 hours. {overnight(eta, tz) ? <>It should be up by morning (around <b className="hm-nw">{fmtTime(eta, tz)}{abbr}</b>).</> : <>Expected around <b className="hm-nw">{fmtTime(eta, tz)}{abbr}</b>.</>}</p>
    <span className="hm-proc" role="progressbar" aria-label="Replay processing" aria-valuetext="Processing"><i /></span>
    {member ? <div className="hm-hero-actions">
      <Btn variant={notify ? "secondary" : "pop"} onDark={notify} icon={notify ? <Check size={17} aria-hidden="true" /> : <Bell size={17} aria-hidden="true" />} aria-pressed={notify} onClick={() => setNotify(n => !n)}>
        {notify ? "We'll email you when it's up" : "Tell me when it's up"}
      </Btn>
    </div> : <HeroLock what="replay" length={s.replay?.length} />}
  </>;
}

function ReplayBlock({ s, member }: { s: Session; member: boolean }) {
  const r = s.replay!;
  const prog = s.progress ?? 0;
  const meta = `${member ? `${fmtDur(r.length)} · ` : ""}${r.chapters.length} chapters${r.fixNotes.length ? ` · ${r.fixNotes.length} fix notes` : ""}${r.messages ? " · chat replay" : ""}`;
  return <>
    <PLink to={P.session(s.id)} className="hm-hero-thumb" aria-label={`${member ? "Watch" : "Preview"} ${s.title}`}>
      <Thumb src={r.poster} duration={r.length} progress={member ? prog / r.length : undefined} play={member} locked={!member} />
    </PLink>
    <p className="hm-hero-body">{meta}</p>
    {member ? <>
      {prog > 0 && <div className="hm-resume"><ProgressBar value={prog / r.length} label="Watched" className="hm-bar-dark" /><span>{fmtLeft(r.length, prog)}</span></div>}
      {r.fixNotes.length > 0 && <p className="hm-hero-fix"><TriangleAlert size={15} aria-hidden="true" />Read first: {r.fixNotes[0].text}</p>}
      <div className="hm-hero-actions">
        <Btn variant="pop" to={P.session(s.id)} icon={<Play size={17} fill="currentColor" aria-hidden="true" />}>{prog ? "Resume" : "Watch replay"}</Btn>
      </div>
    </> : <HeroLock what="replay" length={r.length} preview sessionId={s.id} />}
  </>;
}

function FreeLiveLine() {
  const now = useNow(60000);
  const when = useWhen();
  const yt = upcomingSessions(now, 1, "youtube")[0];
  if (!yt) return null;
  return <a className="hm-hero-yt" href={yt.url} target="_blank" rel="noreferrer">
    <MonitorPlay size={18} aria-hidden="true" />
    <span><b>Free for everyone on YouTube:</b> {yt.title} · {nw(when(yt, now, true))}</span>
    <ExternalLink size={14} aria-hidden="true" />
  </a>;
}

/* ─────────────── Supply checklist ─────────────── */

/** `compact` (desktop rail): shows only what's still to get, so the sticky rail fits the
    viewport; items ticked here stay visible until the list is switched. */
function SupplyChecklist({ project, inSheet, compact }: { project: Project; inSheet?: boolean; compact?: boolean }) {
  const { role } = usePortal();
  const ck = useChecklist(project.id);
  const [list, setListRaw] = useState<"pattern" | "chelseaUsed">("pattern");
  const [all, setAll] = useState(!compact);
  const [kept, setKept] = useState<string[]>([]);
  const setList = (l: "pattern" | "chelseaUsed") => { setListRaw(l); setKept([]); };
  if (!can(role, "supplies")) return <div className="hm-ck-lock">
    <Chip tone="lock" icon={<LockGlyph />}>{lockLine("free")}</Chip>
    <p>Supply lists are free with an account. Tick things off as you gather them.</p>
    <JoinButton need="free" size="sm" />
  </div>;
  const full: Item[] = project.supplies[list];
  const left = full.filter(i => !ck.has(i.id));
  const items = all ? full : full.filter(i => !ck.has(i.id) || kept.includes(i.id));
  const tick = (id: string) => { if (!all) setKept(k => k.includes(id) ? k : [...k, id]); ck.toggle(id); };
  const c = ck.count(project.supplies.pattern);
  return <div className={`hm-ck-wrap${inSheet ? " in-sheet" : ""}`}>
    <div className="hm-ck-sum">
      <span><b>{c.done} of {c.total}</b> gathered</span>
      <ProgressBar value={c.total ? c.done / c.total : 0} label="Supplies gathered" />
    </div>
    <Segmented size="sm" block value={list} onChange={setList} label="Supply list"
      options={[{ value: "pattern", label: "Pattern list" }, { value: "chelseaUsed", label: "Chelsea used" }]} />
    {compact && !all && <p className="hm-ck-left">{left.length ? `Still to get · ${left.length}` : "Everything on this list is gathered"}</p>}
    <ul className="hm-ck-list" aria-label={list === "pattern" ? "Pattern list" : "What Chelsea used"}>
      {items.map(i => {
        const on = ck.has(i.id);
        return <li key={i.id}>
          <button type="button" role="checkbox" aria-checked={on} className={`hm-ck${on ? " is-on" : ""}`} onClick={() => tick(i.id)}>
            <span className="hm-ck-box" aria-hidden="true">{on && <Check size={14} strokeWidth={3} />}</span>
            <span className="hm-ck-main">
              <span className="hm-ck-name">{i.name}</span>
              {(i.qty || i.note || i.optional) && <span className="hm-ck-meta">{[i.qty, i.note, i.optional ? "optional" : ""].filter(Boolean).join(" · ")}</span>}
            </span>
          </button>
        </li>;
      })}
    </ul>
    {compact && <button type="button" className="hm-ck-more" aria-expanded={all} onClick={() => { setAll(a => !a); setKept([]); }}>
      {all ? "Show only what's left" : `Show all ${full.length}`}
    </button>}
  </div>;
}

function RailChecklist({ project }: { project: Project }) {
  return <Card pad="md" className="hm-rail-ck">
    <div className="hm-rail-head"><h2>My supplies</h2><PLink to={`${P.project(project.id)}#supplies`} className="hm-link">{project.title}</PLink></div>
    <SupplyChecklist project={project} compact />
  </Card>;
}

/* ─────────────── This month's project ─────────────── */

function ProjectCard({ project: p, onChecklist }: { project: Project; onChecklist: () => void }) {
  const { role } = usePortal();
  const now = useNow(30000);
  const when = useWhen();
  const ck = useChecklist(p.id);
  const c = ck.count(p.supplies.pattern);
  const member = can(role, "replays");
  const studio = role === "chelsea";
  const sessions = sessionsFor(p.id);
  return <Card pad="none" className="hm-proj" as="article">
    <div className="hm-proj-media">
      <img src={p.hero} alt={p.heroAlt} />
      <Chip tone="neutral" className="hm-proj-month">{p.monthLabel.split(" ")[0]} project</Chip>
    </div>
    <div className="hm-proj-body">
      <header className="hm-proj-head">
        <p className="pt-kicker">This month · {p.level}</p>
        <h3 className="hm-proj-title"><PLink to={P.project(p.id)}>{p.title}</PLink></h3>
        <p className="hm-proj-sub">{p.subtitle}</p>
        {!member && <p className="hm-proj-teaser">{p.teaser}</p>}
      </header>

      <div className="hm-proj-block">
        <a className="hm-tile-row" href={p.pattern.url} target="_blank" rel="noreferrer">
          <span className="hm-ico"><FileText size={18} aria-hidden="true" /></span>
          <span className="hm-tile-row-main"><b>{p.pattern.name}</b><small>{p.pattern.designer} · {p.pattern.price}</small></span>
          <span className="hm-link hm-get">Get it <ExternalLink size={14} aria-hidden="true" /></span>
        </a>
        {p.pattern.code && <CopyCode block code={p.pattern.code} discount={p.pattern.discount} expires={p.pattern.codeExpires} />}
      </div>

      {can(role, "supplies") && !studio && c.total > 0 && <button type="button" className="hm-tile-row" onClick={onChecklist}>
        <span className="hm-ico"><ListChecks size={18} aria-hidden="true" /></span>
        <span className="hm-tile-row-main"><b>{c.done} of {c.total} supplies gathered</b><ProgressBar value={c.done / c.total} label="Supplies gathered" /></span>
        <span className="hm-link">Checklist</span>
      </button>}
      {!can(role, "supplies") && <div className="hm-tile-row is-locked">
        <span className="hm-ico"><ListChecks size={18} aria-hidden="true" /></span>
        <span className="hm-tile-row-main"><b>Supply list · {p.supplies.pattern.filter(i => !i.optional).length} items</b><small>Free with an account</small></span>
        <JoinButton need="free" size="sm" variant="secondary" short />
      </div>}

      <ol className="hm-steps" aria-label="Classes">
        {sessions.map(x => {
          const st = sessionState(x, now);
          const ready = st === "replay" && !!x.replay;
          const prog = ready && member && x.replay ? (x.progress ?? 0) / x.replay.length : 0;
          const locked = ready && !member && !studio;
          const meta = ready && x.replay
            ? studio ? `${fmtDur(x.replay.length)} · ${x.label === "Prep" ? 46 : 18} plays` : member ? (x.progress ? (prog > .97 ? "Watched" : fmtLeft(x.replay.length, x.progress)) : fmtDur(x.replay.length)) : `${fmtDur(x.replay.length)} · 30s preview`
            : st === "live" ? "Happening now" : st === "processing" ? "Replay coming soon" : when(x, now, true);
          return <li key={x.id}>
            <PLink to={P.session(x.id)} className={`hm-step is-${st}`}>
              <span className="hm-step-dot" aria-hidden="true">{ready && member && !studio ? <ProgressRing value={prog} size={30} stroke={3} label="" /> : x.label === "Prep" ? <Scissors size={15} /> : x.label.slice(-1)}</span>
              <span className="hm-step-main"><b>{x.label === "Prep" ? "Prep video" : x.label}</b><small>{meta}</small></span>
              {locked ? <Chip tone="lock" icon={<LockGlyph />} className="hm-step-chip">{PLANS.member.tag}</Chip> : <SessionChip state={st} className="hm-step-chip" />}
            </PLink>
          </li>;
        })}
      </ol>
      <div className="hm-proj-foot">
        <Btn variant="ghost" to={P.project(p.id)} iconEnd={<ArrowRight size={16} aria-hidden="true" />}>Open project</Btn>
        <span className="hm-proj-foot-meta">{plural(makesAt(now, p.id).length, "make")} · {plural(qaFor(p.id, now).length, "question")}</span>
      </div>
    </div>
  </Card>;
}

/* ─────────────── Lists ─────────────── */

function ContinueWatching() {
  const now = useNow(60000);
  const rows = continueWatching(now).slice(0, 3);
  if (!rows.length) return null;
  return <Section title="Continue watching" action={{ label: "All classes", to: P.classes }} id="hm-continue">
    <List>
      {rows.map(x => <Row key={x.id} to={P.session(x.id)}
        lead={<Thumb src={x.replay!.poster} width={96} progress={(x.progress ?? 0) / x.replay!.length} play />}
        title={`${projectOf(x)?.title ?? ""} · ${x.label === "Prep" ? "Prep video" : x.label}`}
        meta={<>{fmtLeft(x.replay!.length, x.progress)} · {fmtDur(x.replay!.length)}</>} />)}
    </List>
  </Section>;
}

function FreeLives() {
  const now = useNow(60000);
  const { tz, viewer: v } = usePortal();
  const when = useWhen();
  const rows = upcomingSessions(now, 3, "youtube");
  if (!rows.length) return null;
  return <Section title="Free lives on YouTube" kicker="Open to everyone" id="hm-yt">
    <List>
      {rows.map(x => <Row key={x.id} href={x.url}
        lead={<span className="hm-ico is-teal"><MonitorPlay size={18} aria-hidden="true" /></span>}
        title={x.title}
        meta={when(x, now, true)}
        trail={<ExternalLink size={15} aria-hidden="true" />} chevron={false} />)}
    </List>
  </Section>;
}

function FromChelsea({ title = "From Chelsea" }: { title?: string }) {
  const { role } = usePortal();
  const now = useNow(60000);
  const pin = pinnedNote(now);
  // Logistics notes about a class that has already started ("Going live in a few minutes!!") are stale on Home.
  const stale = (n: Note) => { const x = n.kind === "logistics" ? getSession(n.sessionId) : undefined; return !!x && !x.recorded && x.start <= now; };
  const notes = notesAt(now).filter(n => n.id !== pin?.id && !stale(n) && (can(role, "notes") || n.need === "visitor")).slice(0, 3);
  if (!notes.length) return null;
  return <Section title={title} kicker="Latest notes" action={can(role, "notes") ? { label: "All notes", to: `${P.community}#notes` } : undefined} id="hm-notes">
    <ul className="hm-notes">
      {notes.map(n => <li key={n.id} className="hm-note">
        <Avatar person="chelsea" size={34} />
        <div><p className="hm-note-meta"><b>Chelsea</b> · {ago(n.at, now)}{n.projectId && <> · {getProject(n.projectId)?.title}</>}</p><p className="hm-note-text">{n.text}</p></div>
      </li>)}
    </ul>
  </Section>;
}

function NewSince() {
  const { role, tz } = usePortal();
  const now = useNow(60000);
  const ns = newSince(now);
  type It = { key: string; at: Date; lead: ReactNode; title: ReactNode; meta: ReactNode; to: string; trail?: ReactNode };
  const items: It[] = [
    ...ns.answers.map(q => ({ key: q.id, at: q.answer!.at, to: `${P.project(q.projectId)}#qa`,
      lead: <span className="hm-ico is-peri"><MessageCircleReply size={18} aria-hidden="true" /></span>,
      title: <>Chelsea answered “{q.text}”</>, meta: <>Q&amp;A · {ago(q.answer!.at, now)}</> })),
    ...ns.makes.map(m => ({ key: m.id, at: m.at, to: `${P.community}#makes`,
      lead: <Thumb src={m.img} ratio="1/1" width={44} />,
      title: <>{who(m.by).name.split(" ")[0]} shared a make: {m.caption}</>, meta: <>Make · {getProject(m.projectId)?.title} · {ago(m.at, now)}</> })),
    ...ns.posts.map(p => ({ key: p.id, at: p.at, to: `${P.classes}#extras`,
      lead: <span className="hm-ico is-gold"><PenLine size={18} aria-hidden="true" /></span>,
      title: p.title, meta: <>Post · {p.minutes} min read · {ago(p.at, now)}</>,
      trail: atLeast(role, p.need) ? undefined : <Chip tone="lock" icon={<LockGlyph />}>{p.need === "member" ? PLANS.member.tag : PLANS.friend.tag}</Chip> })),
  ].sort((a, b) => b.at.getTime() - a.at.getTime());
  const since = fmtDay(ME.lastVisit, tz, { weekday: "short", month: "short", day: "numeric" });
  return <Section title="New since you last visited" kicker={`Since ${since}`} action={{ label: "Community", to: P.community }} id="hm-new">
    {items.length ? <List>
      {items.slice(0, 5).map(i => <Row key={i.key} to={i.to} lead={i.lead} title={i.title} meta={i.meta} trail={i.trail} />)}
    </List> : <Empty icon={<Sparkles size={22} aria-hidden="true" />} hand="all caught up!" title="Nothing new since your last visit" body="New makes, answers and posts show up here." />}
  </Section>;
}

function PastShelf() {
  const { role } = usePortal();
  const now = useNow(60000);
  const member = can(role, "replays");
  const projects = pastProjects(now);
  return <Section title="Past projects" action={{ label: "All classes", to: P.classes }} id="hm-past">
    <ul className="hm-shelf">
      {projects.map(p => {
        const r = projectRuntime(p, now);
        return <li key={p.id}>
          <PLink to={P.project(p.id)} className="hm-shelf-card">
            <Thumb src={p.hero} alt="" ratio="4/3" locked={!member} />
            <span className="hm-shelf-body">
              <span className="hm-shelf-k">{shortMonth(p)}</span>
              <span className="hm-shelf-t">{p.title}</span>
              <span className="hm-shelf-m">{r.classes} classes · {fmtDur(r.total)}</span>
            </span>
            {member && <ProgressRing value={r.ratio} size={30} stroke={3} className="hm-shelf-ring" />}
          </PLink>
        </li>;
      })}
      <li><PLink to={P.classes} className="hm-shelf-card hm-shelf-all"><span>Every class</span><ArrowRight size={20} aria-hidden="true" /></PLink></li>
    </ul>
  </Section>;
}

/* ─────────────── Visitor ─────────────── */

function Welcome() {
  return <Card tone="paper" pad="lg" tape="pink" tapeSide="right" className="hm-welcome" as="section">
    <Polaroid src="/chelsea-rainbow-portrait.jpg" caption="see you in class!" tilt={4} className="hm-welcome-pic" />
    <Hand>come sew with us!</Hand>
    <h1 className="hm-welcome-title">Loose Threads Club</h1>
    <p className="hm-welcome-body">One project a month with Chelsea: two live classes, every replay with chapters, and a friendly room of makers.</p>
    <div className="hm-hero-actions">
      <JoinButton need="member" />
      <Btn variant="secondary" to={`${P.join}?plan=free`}>Create a free account</Btn>
    </div>
    <p className="hm-welcome-sign">Already a member? <PLink to={P.me}>Sign in</PLink></p>
  </Card>;
}

function Plans() {
  const rows: [string, boolean, boolean, boolean][] = [
    ["Notes from Chelsea, free YouTube lives", true, true, true],
    ["Studio posts, share your makes", false, true, true],
    ["2 live classes a month", false, false, true],
    ["Every replay, chapters & fix notes", false, false, true],
    ["Pattern discount codes", false, false, true],
  ];
  const yes = <Check size={16} strokeWidth={3} aria-label="Included" />;
  const no = <span className="hm-plans-no" aria-label="Not included">–</span>;
  return <Card pad="md" className="hm-plans" as="section">
    <h2 className="hm-rail-h">Plans</h2>
    <table>
      <thead><tr><th scope="col"><span className="hm-sr">Benefit</span></th><th scope="col">Free</th><th scope="col">{PLANS.friend.name}<small>$3</small></th><th scope="col" className="is-hi">Member<small>$25</small></th></tr></thead>
      <tbody>{rows.map(([l, a, b, c]) => <tr key={l}><th scope="row">{l}</th><td>{a ? yes : no}</td><td>{b ? yes : no}</td><td className="is-hi">{c ? yes : no}</td></tr>)}</tbody>
    </table>
    <Btn to={`${P.join}?plan=member`} block>Compare plans</Btn>
  </Card>;
}

/* ─────────────── Chelsea: Studio "Today" ─────────────── */

function StudioToday() {
  const { tz } = usePortal();
  const now = useNow(1000);
  const h = heroSession(now);
  const s = h?.session, state = h?.state;
  const proj = currentProject(now);
  const [sheet, setSheet] = useState(false);
  const [streaming, setStreaming] = useState<"idle" | "live" | "ended">("idle");
  const [notify, setNotify] = useState(true);
  const upNext = state === "processing" || state === "replay" ? nextClubSession(now) : s;
  const cd = useCountdown(upNext?.start ?? now);
  const unanswered = qaFor(proj.id, now).filter(q => !q.answer);
  const isLive = state === "live" ? streaming !== "ended" : streaming === "live";
  const liveFor = s ? Math.max(0, Math.round((now.getTime() - s.start.getTime()) / 6e4)) : 0;

  const replay = (() => {
    const last = sessionsFor(proj.id).filter(x => x.label !== "Prep" && (sessionState(x, now) === "processing" || sessionState(x, now) === "replay")).pop();
    if (isLive) return { v: "Recording", m: "Saves and attaches to Witch Hat automatically", tone: "live" as const };
    if (last && sessionState(last, now) === "processing") return { v: "Processing", m: `${last.label} · auto-publishes ~${fmtTime(new Date(sessionEnd(last).getTime() + 3 * 36e5), tz)} MT, notifications on`, tone: "peri" as const };
    if (last) return { v: "Published", m: `${last.label} · 18 plays · 9 chapters`, tone: "ok" as const };
    return { v: "Prep is up", m: "Witch Hat prep · 46 plays", tone: "ok" as const };
  })();

  return <><Card tone="ink" pad="lg" tape="peri" tapeSide="left" className="hm-studio" as="section">
    <div className="hm-hero-tags"><Chip tone="peri">Studio</Chip><span className="pt-kicker">Today · {fmtDay(now, tz, { weekday: "long", month: "short", day: "numeric" })}</span></div>

    {s && <div className="hm-studio-next">
      <div className="hm-studio-next-main">
        <p className="hm-studio-k">{isLive ? "You're live" : "Next live"}</p>
        <h2 className="hm-hero-title">{(isLive ? s : upNext ?? s).title}</h2>
        <p className="hm-hero-when"><CalendarClock size={16} aria-hidden="true" />
          {isLive ? <>On air {state === "live" ? `${liveFor}m` : "now"} · 11 watching · 112 messages</>
            : upNext && <>{relDay(upNext.start, now, tz)} · {fmtTime(upNext.start, tz)} MT{!cd.done ? ` · in ${cd.label}` : ""}</>}
        </p>
      </div>
      <div className="hm-hero-actions">
        {isLive
          ? <><Btn variant="live" to={P.session(s.id)} icon={<Radio size={18} aria-hidden="true" />}>Open live room</Btn>
              <Btn variant="secondary" onDark onClick={() => setStreaming("ended")}>End stream</Btn></>
          : streaming === "ended" ? <Chip tone="ok" icon={<Check size={13} aria-hidden="true" />}>Stream ended · replay processing</Chip>
          : <Btn variant="live" icon={<Radio size={18} aria-hidden="true" />} onClick={() => setSheet(true)} aria-haspopup="dialog">Go live</Btn>}
        <Btn variant="secondary" onDark to={P.project(proj.id)} icon={<PenLine size={16} aria-hidden="true" />}>Edit project</Btn>
      </div>
    </div>}

    <ul className="hm-tiles" aria-label="Today at a glance">
      <li><span className="hm-tile-k"><Hourglass size={14} aria-hidden="true" />Replay</span><b>{replay.v}</b><small>{replay.m}</small></li>
      <li><span className="hm-tile-k"><CircleHelp size={14} aria-hidden="true" />Unanswered Q&amp;A</span><b>{unanswered.length}</b><small>{unanswered.length ? `Oldest ${ago(unanswered[unanswered.length - 1].at, now)}` : "All answered"}</small></li>
      <li><span className="hm-tile-k"><Users size={14} aria-hidden="true" />New members</span><b>+3</b><small>This week: 2 Members, 1 Thread Friend (plus 4 free)</small></li>
      <li><span className="hm-tile-k"><TriangleAlert size={14} aria-hidden="true" />Cancelled</span><b>1</b><small>This week · “time zone / schedule”</small></li>
    </ul>
  </Card>

    <Sheet open={sheet} onClose={() => setSheet(false)} title="Go live" footer={<>
      <Btn variant="live" block size="lg" icon={<Radio size={18} aria-hidden="true" />} onClick={() => { setStreaming("live"); setSheet(false); }}>Start stream</Btn>
      <p className="hm-sheet-note">Prototype: nothing is broadcast or sent.</p>
    </>}>
      <List flush>
        <Row title={s?.title ?? "Live class"} meta={s ? `${fmtDay(s.start, tz)} · ${fmtTime(s.start, tz)} MT` : ""} />
        <Row title="Video source" meta="OBS · stream key ready" trail={<Chip tone="ok" icon={<Check size={12} aria-hidden="true" />}>Connected</Chip>} />
        <Row title="Who can watch" meta="Members ($25) · 34 people" trail={<Chip tone="teal">Members</Chip>} />
        <Row title="Chat" meta="On · saved for the chat replay" trail={<Chip tone="ok">On</Chip>} />
        <Row title="Tell members it's starting" meta="Email + push to 34 members" onClick={() => setNotify(n => !n)} chevron={false}
          trail={<span className={`hm-switch${notify ? " is-on" : ""}`} role="switch" aria-checked={notify} aria-label="Notify members"><i /></span>} />
      </List>
    </Sheet>
  </>;
}

function NeedsYou() {
  const { tz } = usePortal();
  const now = useNow(60000);
  const proj = currentProject(now);
  const unanswered = qaFor(proj.id, now).filter(q => !q.answer);
  const fresh = makesAt(now, proj.id).filter(m => m.by !== "chelsea").slice(0, 2);
  const nextYt = upcomingSessions(now, 1, "youtube")[0];
  return <Section title="Needs you" id="hm-needs">
    <List>
      {unanswered.map(q => <Row key={q.id} to={`${P.project(q.projectId)}#qa`}
        lead={<Avatar person={q.by} size={36} />}
        title={q.text} meta={<><b className="hm-flag">Unanswered</b> · {who(q.by).name} · {ago(q.at, now)} · {q.votes} votes</>} />)}
      {fresh.length > 0 && <Row to={`${P.community}#makes`} lead={<Thumb src={fresh[0].img} ratio="1/1" width={36} />}
        title={`${fresh.length} new makes to feature`} meta={fresh.map(m => who(m.by).name).join(", ")} />}
      {nextYt && <Row lead={<span className="hm-ico is-teal"><MonitorPlay size={18} aria-hidden="true" /></span>}
        title={`Free live · ${nextYt.title}`} meta={`${fmtDay(nextYt.start, tz)} · ${fmtTime(nextYt.start, tz)} MT · YouTube link set`} href={nextYt.url} />}
    </List>
  </Section>;
}

function MembersCard() {
  return <Card pad="md" className="hm-members" as="section">
    <h2 className="hm-rail-h">Members</h2>
    <dl className="hm-members-dl">
      <div><dt>Members ($25)</dt><dd>34</dd></div>
      <div><dt>Thread Friends ($3)</dt><dd>4</dd></div>
      <div><dt>Free accounts</dt><dd>34</dd></div>
    </dl>
    <p className="hm-members-note">Free accounts are your upsell pool. A note pinned for “everyone” reaches all 72.</p>
    <Btn variant="secondary" size="sm" to={P.me}>Members &amp; billing</Btn>
  </Card>;
}

/* ─────────────── Page ─────────────── */

/** The shell's rail is sticky at the top. When the Next-up card + checklist are taller than
    the window, a plain top-sticky rail would hide its bottom until the page ends, so we
    pull `top` up just enough for the rail to scroll with the page and then stick with its
    bottom in view. Desktop only; phones hide the rail. */
function useRailFit(key: string) {
  useEffect(() => {
    const rail = document.querySelector<HTMLElement>(".hm .pt-rail");
    if (!rail) return;
    const fit = () => {
      if (innerWidth < 900) { rail.style.top = ""; return; }
      const shell = rail.closest<HTMLElement>(".pt") ?? document.documentElement;
      const liveH = parseFloat(getComputedStyle(shell).getPropertyValue("--pt-live-h")) || 0;
      const base = 24 + liveH;
      const room = innerHeight - rail.offsetHeight - 64; // clear of the review pill
      rail.style.top = room < base ? `${Math.round(room)}px` : "";
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(rail);
    addEventListener("resize", fit);
    return () => { ro.disconnect(); removeEventListener("resize", fit); rail.style.top = ""; };
  }, [key]);
}

export function PortalHome() {
  const { role, tz, viewer: v } = usePortal();
  const now = useNow(60000);
  const project = currentProject(now);
  const [sheet, setSheet] = useState(false);
  const openCk = () => setSheet(true);
  const { clock } = usePortal();
  useRailFit(`${role}:${clock}`);

  const hello = role !== "visitor" && <header className="hm-hello">
    <p className="pt-kicker">{fmtDay(now, tz, { weekday: "long", month: "long", day: "numeric" })}</p>
    <h1 className="hm-hello-h">{greeting(now, tz)}, {role === "chelsea" ? "Chelsea" : v?.first ?? ME.first}</h1>
  </header>;

  const sheetEl = <Sheet open={sheet} onClose={() => setSheet(false)} title={`${project.title} supplies`}
    footer={<Btn variant="secondary" block to={`${P.project(project.id)}#supplies`}>Full supply list &amp; notes</Btn>}>
    <SupplyChecklist project={project} inSheet />
  </Sheet>;

  if (role === "chelsea") return <PortalShell active="home" className="hm" railPhone="hide"
    rail={<><MembersCard /><ComingUp /></>}>
    {hello}
    <StudioToday />
    <NeedsYou />
    <ProjectCard project={project} onChecklist={openCk} />
    <FromChelsea title="Your recent notes" />
    <PastShelf />
    {sheetEl}
  </PortalShell>;

  if (role === "visitor") return <PortalShell active="home" className="hm" railPhone="hide"
    rail={<><NextUp onChecklist={openCk} /><Plans /></>}>
    <Welcome />
    <div className="hm-phone"><NextUp onChecklist={openCk} /></div>
    <Section title="This month's project" id="hm-proj-v"><ProjectCard project={project} onChecklist={openCk} /></Section>
    <FromChelsea />
    <PastShelf />
    <div className="hm-phone"><Plans /></div>
    {sheetEl}
  </PortalShell>;

  const member = can(role, "replays");
  return <PortalShell active="home" className="hm" railPhone="hide"
    rail={<><NextUp onChecklist={openCk} /><RailChecklist project={project} /></>}>
    {hello}
    <div className="hm-phone"><NextUp onChecklist={openCk} /></div>
    <ProjectCard project={project} onChecklist={openCk} />
    {member ? <ContinueWatching /> : <FreeLives />}
    <div className="hm-pair">
      <FromChelsea />
      <NewSince />
    </div>
    <PastShelf />
    {sheetEl}
  </PortalShell>;
}

/** Chelsea's rail: the upcoming schedule at a glance (club + free lives). */
function ComingUp() {
  const { tz } = usePortal();
  const now = useNow(60000);
  const rows = upcomingSessions(now, 4);
  return <Card pad="md" as="section" className="hm-sched">
    <div className="hm-rail-head"><h2>Coming up</h2><PLink to={`${P.classes}#schedule`} className="hm-link">Schedule</PLink></div>
    <List flush>
      {rows.map(x => <Row key={x.id} to={x.kind === "club" ? P.session(x.id) : undefined} href={x.kind === "youtube" ? x.url : undefined}
        title={x.kind === "club" ? x.title : `Free live · ${x.title}`}
        meta={`${fmtDay(x.start, tz)} · ${fmtTime(x.start, tz)} MT`}
        trail={x.kind === "club" ? <Chip tone="teal">Club</Chip> : <Chip tone="outline">YouTube</Chip>} chevron={false} />)}
    </List>
  </Card>;
}
